import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  SITE_URL,
  SITE_OG_IMAGE,
  buildCanonicalUrl,
  buildStructuredDataGraph,
  getSeoCopy,
} from '@/data/seo';
import type { LocaleCode } from '@/data/types';
import type { SeoPageKey } from '@/data/seo';

const STRUCTURED_DATA_ID = 'ld-graph';

function setMetaContent(selector: string, content: string) {
  const el = document.head.querySelector<HTMLMetaElement>(selector);
  if (el) {
    el.setAttribute('content', content);
  }
}

function setLinkHref(selector: string, href: string) {
  const el = document.head.querySelector<HTMLLinkElement>(selector);
  if (el) {
    el.setAttribute('href', href);
  }
}

function setStructuredData(locale: LocaleCode) {
  const payload = JSON.stringify(buildStructuredDataGraph(locale));
  let node = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null;

  if (!node) {
    node = document.createElement('script');
    node.id = STRUCTURED_DATA_ID;
    node.type = 'application/ld+json';
    document.head.appendChild(node);
  }

  node.textContent = payload;
}

function resolveSeoPage(routePath: string, metaPage?: unknown): SeoPageKey {
  if (metaPage === 'about' || metaPage === 'projects' || metaPage === 'uses' || metaPage === 'home') {
    return metaPage;
  }
  if (routePath.startsWith('/about')) return 'about';
  if (routePath.startsWith('/projects')) return 'projects';
  if (routePath.startsWith('/uses')) return 'uses';
  return 'home';
}

function applySeo(locale: LocaleCode, routePath: string, metaPage?: unknown) {
  const page = resolveSeoPage(routePath, metaPage);
  const copy = getSeoCopy(locale, page);
  const canonical = buildCanonicalUrl(routePath);
  const ogImage = `${SITE_URL}${SITE_OG_IMAGE}`;

  document.title = copy.title;
  setMetaContent('meta[name="description"]', copy.description);
  setMetaContent('meta[name="robots"]', 'index, follow, max-image-preview:large');
  setLinkHref('link[rel="canonical"]', canonical);
  setMetaContent('meta[property="og:type"]', page === 'home' ? 'profile' : 'website');
  setMetaContent('meta[property="og:title"]', copy.title);
  setMetaContent('meta[property="og:description"]', copy.description);
  setMetaContent('meta[property="og:url"]', canonical);
  setMetaContent('meta[property="og:image"]', ogImage);
  setMetaContent('meta[name="twitter:title"]', copy.title);
  setMetaContent('meta[name="twitter:description"]', copy.description);
  setMetaContent('meta[name="twitter:image"]', ogImage);

  setStructuredData(locale);
}

/**
 * Mantém title, meta description, canonical, Open Graph, Twitter Card
 * e JSON-LD sincronizados com locale e rota atuais.
 */
export function useDocumentSeo() {
  const { locale } = useI18n();
  const route = useRoute();

  watch(
    [locale, () => route.path, () => route.meta.seoPage],
    ([value, path, seoPage]) => {
      applySeo(value as LocaleCode, path, seoPage);
    },
    { immediate: true },
  );
}
